import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'

// MUI Components
import {
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'

// Utils and Redux
import { addTemporaryContact } from '../../redux/contactRedux'
import { cleanUserAgent } from '../../utils/userAgent'
import { amountOptions, loanReasonOptions } from '../../utils/options'
import { checkAge } from '../../utils/validationUtils'
import { saveLanderPrefillFromSearch } from '../../utils/landerPrefill'

// Components
import { RatesFee } from '../popups/RatesFee/RatesFee'

// Styles
import styles from './HeroForm.module.css'
import {
    MenuProps,
    btnSubmitCustom,
    labelCustom,
    menuItemCustom,
    selectCustom
} from '../Banner/style'

// Images
import securedCertificate from '../../assets/images/security/256-bit-encrypting.png'
import sslSecured from '../../assets/images/security/aes-256-ssl.png'
import sectigoSecured from '../../assets/images/security/sectigo-seal.png'

const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
]

// New Custom Styles matching the design
const newSelectCustom = {
    ...selectCustom,
    borderRadius: "100px !important", // Pill shape
    backgroundColor: "#00000026", // Semi-transparent bluish dark
    color: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "& .MuiSelect-icon": {
        color: "#fff",
    },
    "& .MuiSelect-select": {
        color: "#fff",
    }
}

const newBtnSubmitCustom = {
    ...btnSubmitCustom,
    backgroundColor: "#D80027", // Red color from image
    borderRadius: "50px",
    fontSize: "24px",
    fontWeight: "800",
    textTransform: "uppercase",
    height: "60px",
    boxShadow: "0 4px 15px rgba(216, 0, 39, 0.4)",
    "&:hover": {
        backgroundColor: "#b30020",
    }
}

export const HeroForm = () => {
    const isMobile = window.innerWidth < 768

    const [openRateFee, setOpenRateFee] = useState(false)
    const [loadingButton, setLoadingButton] = useState(false)
    const [isSelectOpen, setIsSelectOpen] = useState(false)

    // Redux state
    const contactRedux = useSelector((state) => state?.contact?.contact)
    const temporaryContact = useSelector((state) => state?.contact?.temporary)

    // Parse + persist lander data from the URL once, so every step can pre-fill.
    const landerPrefillRef = useRef(null)
    if (landerPrefillRef.current === null) {
        landerPrefillRef.current = saveLanderPrefillFromSearch(window.location.search)
    }
    const landerPrefill = landerPrefillRef.current

    const [amount, setAmount] = useState(() => {
        // landerPrefill already normalized amount/loanAmount/loan_amount.
        const fromLander = landerPrefill?.amount
        const validLander = amountOptions.some((o) => String(o.value) === String(fromLander))
            ? fromLander
            : null
        // Saved value (redux) wins; the URL param is only a fallback seed.
        return (
            temporaryContact?.amount ??
            temporaryContact?.loanAmount ??
            contactRedux?.amount ??
            contactRedux?.loanAmount ??
            validLander ??
            amountOptions[9]?.value
        )
    })

    const jornayaRef = useRef(null)
    const [jornayaLeadId, setJornayaLeadId] = useState('')

    const [loanReason, setLoanReason] = useState(() => {
        // landerPrefill already normalized loanReason/loan_reason/reason (uppercased).
        const fromLander = landerPrefill?.loanReason
        const validLander = loanReasonOptions.some((o) => o.value === fromLander)
            ? fromLander
            : null
        // Saved value (redux) wins; the URL param is only a fallback seed.
        return (
            temporaryContact?.loanReason ??
            contactRedux?.loanReason ??
            validLander ??
            loanReasonOptions[3].value
        )
    })

    // DOB state
    const [dob, setDob] = useState({
        month: temporaryContact?.dobMonth ?? landerPrefill?.dobMonth ?? (contactRedux?.dob
            ? dayjs(contactRedux?.dob, 'YYYY-MM-DD').format('MM')
            : ''),
        day: temporaryContact?.dobDay ?? landerPrefill?.dobDay ?? (contactRedux?.dob
            ? dayjs(contactRedux?.dob, 'YYYY-MM-DD').format('DD')
            : ''),
        year: temporaryContact?.dobYear ?? landerPrefill?.dobYear ?? (contactRedux?.dob
            ? dayjs(contactRedux?.dob, 'YYYY-MM-DD').format('YYYY')
            : ''),
        totalDayInMonth: 31
    })
    const [openMonthOptions, setOpenMonthOptions] = useState(false)
    const [openDayOptions, setOpenDayOptions] = useState(false)
    const [openYearOptions, setOpenYearOptions] = useState(false)

    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const emailFromURL = searchParams.get('email')
    const loanReasonFromURL = searchParams.get('loan_reason')

    const dispatch = useDispatch()
    const navigate = useNavigate()

    // set sessionStorage
    if (loanReasonFromURL) {
        sessionStorage.setItem('loanReasonFromURL', loanReasonFromURL)
    }

    // Check total day in month and age validation
    useEffect(() => {
        if (dob.month) {
            const isValidMonth = dayjs(dob.month, 'MM').isValid()
            if (isValidMonth) {
                const totalDayInMonth = dayjs(`2000-${dob.month}`, 'YYYY-MM').daysInMonth()
                setDob((prev) => ({ ...prev, totalDayInMonth }))
            }
        }

        if (dob.year && dob.month && dob.day) {
            const age = checkAge(dob.year)
            if (age < 18) {
                setError('age', {
                    type: 'manual',
                    message: 'You must be at least 18 years old'
                })
            } else {
                clearErrors('age')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dob.month, dob.year, dob.day])

    // Prefill from URL params (whichever are present) without auto-advancing.
    // amount & loan_reason prefill their selects via the state initializers above;
    // email has no field on this page, so stash it for the /email step.
    useEffect(() => {
        if (emailFromURL) {
            sessionStorage.setItem('prefill_email', emailFromURL)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailFromURL])

    // jornaya lead id
    useEffect(() => {
        const checkValue = () => {
            const value = jornayaRef.current?.value
            if (value) {
                setJornayaLeadId(value)
            } else {
                setTimeout(checkValue, 1000)
            }
        }
        checkValue()
        return () => { }
    }, [])

    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onChange'
    })

    const onSubmit = async (data) => {
        if (loadingButton) {
            return false
        }

        // Validate DOB
        if (!dob.month || !dob.day || !dob.year) {
            if (!dob.month) setError('month', { type: 'required', message: 'Month is required!' })
            if (!dob.day) setError('day', { type: 'required', message: 'Day is required!' })
            if (!dob.year) setError('year', { type: 'required', message: 'Year is required!' })
            return false
        }

        // Check age
        const age = checkAge(dob.year)
        if (age < 18) {
            setError('age', { type: 'manual', message: 'You must be at least 18 years old' })
            return false
        }

        try {
            setLoadingButton(true)

            const dobString = `${dob.year}-${dob.month}-${dob.day}`
            dispatch(
                addTemporaryContact({
                    ...data,
                    loanAmount: amount,
                    userAgent: cleanUserAgent(navigator.userAgent),
                    jornayaLeadId: jornayaLeadId || undefined,
                    dob: dobString,
                    dobMonth: dob.month,
                    dobDay: dob.day,
                    dobYear: dob.year
                })
            )
            setLoadingButton(false)
            navigate(`/lgname`)
        } catch (error) {
            console.error('An error occurred:', error)
            setLoadingButton(false)
        }
    }

    // DOB styles - slightly adjusted for 3 small fields but consistent with main inputs
    const dobSelectStyle = {
        ...newSelectCustom,
        borderRadius: "12px", // Less rounded for side-by-side or keep pill? Pill might be too round for small width.
        // Let's keep it semi-rounded
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
    }

    return (
        <>
            {openRateFee && (
                <RatesFee openRateFee={openRateFee} setOpenRateFee={setOpenRateFee} />
            )}
            <div className={styles.heroFormWrapper}>
                {/* Glass Container for Header and Inputs */}
                <div className={styles.glassContainer}>
                    <div className={styles.heading}>
                        <h1 className={styles.title}>A SMARTER WAY TO BORROW</h1>
                        <p className={styles.subtitle}>Get cash fast, deposited right to your bank.</p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className={styles.formInputs}>
                        {/* Loan Amount */}
                        <Controller
                            name="amount"
                            defaultValue={amount}
                            control={control}
                            rules={{ required: 'Loan amount is required!' }}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    error={!!errors?.amount}
                                    size={isMobile ? 'small' : 'medium'}
                                    className={styles.formControl}
                                >
                                    <FormHelperText sx={labelCustom} className={styles.inputLabel}>
                                        How much would you like to borrow? ($100 - $15,000) *
                                    </FormHelperText>
                                    <Select
                                        {...field}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            field.onChange(value)
                                            setAmount(value)
                                        }}
                                        value={amount}
                                        displayEmpty
                                        MenuProps={{
                                            ...MenuProps,
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'center'
                                            }
                                        }}
                                        sx={newSelectCustom}
                                        onClose={() => setIsSelectOpen(false)}
                                        onOpen={() => setIsSelectOpen(true)}
                                        className={isSelectOpen ? 'show' : ''}
                                    >
                                        {amountOptions.map((option, index) => (
                                            <MenuItem
                                                key={index}
                                                value={option.value}
                                                sx={menuItemCustom}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {errors?.amount && errors?.amount?.message}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />

                        {/* Loan Reason */}
                        <Controller
                            name="loanReason"
                            defaultValue={loanReason}
                            control={control}
                            rules={{ required: 'Loan reason is required!' }}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    error={!!errors?.loanReason}
                                    size={isMobile ? 'small' : 'medium'}
                                    className={styles.formControl}
                                >
                                    <FormHelperText sx={labelCustom} className={styles.inputLabel}>
                                        What is the reason for your loan? *
                                    </FormHelperText>
                                    <Select
                                        {...field}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            field.onChange(value)
                                            setLoanReason(value)
                                        }}
                                        value={loanReason}
                                        displayEmpty
                                        MenuProps={MenuProps}
                                        sx={newSelectCustom}
                                        onClose={() => setIsSelectOpen(false)}
                                        onOpen={() => setIsSelectOpen(true)}
                                        className={isSelectOpen ? 'show' : ''}
                                    >
                                        {loanReasonOptions.map((option, index) => (
                                            <MenuItem
                                                key={index}
                                                value={option.value}
                                                sx={menuItemCustom}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {errors?.loanReason && errors?.loanReason?.message}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />

                        {/* DOB Fields */}
                        <FormHelperText sx={labelCustom} className={styles.inputLabel}>
                            Date of birth *
                        </FormHelperText>
                        <Box className={styles.dobContainer}>
                            {/* MONTH */}
                            <Controller
                                name="month"
                                defaultValue={dob.month}
                                control={control}
                                rules={{ required: 'Month is required!' }}
                                render={({ field: { ref, ...field } }) => (
                                    <FormControl
                                        error={!!errors?.month}
                                        size={isMobile ? 'small' : 'medium'}
                                        fullWidth
                                    >
                                        <Select
                                            {...field}
                                            inputRef={ref}
                                            defaultValue=""
                                            onChange={(e) => {
                                                const value = e.target.value
                                                field.onChange(value)
                                                setDob((prev) => ({
                                                    ...prev,
                                                    month: value
                                                }))
                                                clearErrors('month')
                                            }}
                                            value={dob.month}
                                            displayEmpty
                                            MenuProps={MenuProps}
                                            sx={{
                                                ...dobSelectStyle,
                                                borderRadius: openMonthOptions
                                                    ? '12px 0 0 0'
                                                    : '12px 0 0 12px',
                                                '& fieldset': {
                                                    borderRight: 'none'
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    ...dobSelectStyle['& .MuiOutlinedInput-notchedOutline'],
                                                    borderRadius: '12px 0 0 12px'
                                                },
                                            }}
                                            onClose={() => setOpenMonthOptions(false)}
                                            onOpen={() => setOpenMonthOptions(true)}
                                            className={openMonthOptions ? 'show' : ''}
                                            renderValue={(selected) => {
                                                return !selected ? 'MM' : selected
                                            }}
                                        >
                                            {monthOptions.map((option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={option.value}
                                                    sx={menuItemCustom}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {errors?.month && errors?.month?.message}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                            {/* DAY */}
                            <Controller
                                name="day"
                                defaultValue={dob.day}
                                control={control}
                                rules={{ required: 'Day is required!' }}
                                render={({ field }) => (
                                    <FormControl
                                        error={!!errors?.day}
                                        size={isMobile ? 'small' : 'medium'}
                                        fullWidth
                                        disabled={!dob.month}
                                    >
                                        <Select
                                            {...field}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                field.onChange(value)
                                                setDob((prev) => ({
                                                    ...prev,
                                                    day: value
                                                }))
                                                clearErrors('day')
                                            }}
                                            value={dob.day}
                                            displayEmpty
                                            defaultValue=""
                                            MenuProps={MenuProps}
                                            sx={{
                                                ...dobSelectStyle,
                                                borderRadius: 0,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    ...dobSelectStyle['& .MuiOutlinedInput-notchedOutline'],
                                                    borderRadius: 0
                                                }
                                            }}
                                            onClose={() => setOpenDayOptions(false)}
                                            onOpen={() => setOpenDayOptions(true)}
                                            className={openDayOptions ? 'show' : ''}
                                            renderValue={(selected) => {
                                                return !selected ? 'DD' : selected
                                            }}
                                        >
                                            {[...Array(Number(dob.totalDayInMonth)).keys()].map(
                                                (number) => {
                                                    const dayValue = number + 1
                                                    const formattedDay =
                                                        dayValue < 10
                                                            ? '0' + dayValue
                                                            : dayValue.toString()
                                                    return (
                                                        <MenuItem
                                                            key={number}
                                                            value={formattedDay}
                                                            sx={menuItemCustom}
                                                        >
                                                            {formattedDay}
                                                        </MenuItem>
                                                    )
                                                }
                                            )}
                                        </Select>
                                        <FormHelperText>
                                            {errors?.day && errors?.day?.message}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                            {/* YEAR */}
                            <Controller
                                name="year"
                                defaultValue={dob.year}
                                control={control}
                                rules={{ required: 'Year is required!' }}
                                render={({ field }) => (
                                    <FormControl
                                        error={!!errors?.year}
                                        size={isMobile ? 'small' : 'medium'}
                                        fullWidth
                                        disabled={!dob.day}
                                    >
                                        <Select
                                            {...field}
                                            defaultValue=""
                                            onChange={(e) => {
                                                const value = e.target.value
                                                field.onChange(value)
                                                setDob((prev) => ({
                                                    ...prev,
                                                    year: String(value)
                                                }))
                                                clearErrors('year')
                                            }}
                                            value={dob.year}
                                            displayEmpty
                                            MenuProps={MenuProps}
                                            sx={{
                                                ...dobSelectStyle,
                                                borderRadius: openYearOptions
                                                    ? '0 12px 0 0'
                                                    : '0 12px 12px 0',
                                                '& fieldset': {
                                                    borderLeft: 'none'
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    ...dobSelectStyle['& .MuiOutlinedInput-notchedOutline'],
                                                    borderRadius: '0 12px 12px 0'
                                                }
                                            }}
                                            onClose={() => setOpenYearOptions(false)}
                                            onOpen={() => setOpenYearOptions(true)}
                                            className={openYearOptions ? 'show' : ''}
                                            renderValue={(selected) => {
                                                return !selected ? 'YYYY' : selected
                                            }}
                                        >
                                            {[...Array(100).keys()].map((number) => (
                                                <MenuItem
                                                    key={number}
                                                    value={2008 - number}
                                                    sx={menuItemCustom}
                                                >
                                                    {2008 - number}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {errors?.year && errors?.year?.message}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Box>
                        {errors?.age && (
                            <FormHelperText
                                error
                                style={{
                                    marginTop: '-10px',
                                    marginBottom: '5px'
                                }}
                            >
                                {errors?.age?.message}
                            </FormHelperText>
                        )}

                        {/* jornaya */}
                        <input
                            ref={jornayaRef}
                            id="leadid_token"
                            name="universal_leadid"
                            type="hidden"
                            defaultValue=""
                        />

                        <div style={{fontSize: '12px', color: '#ffffff'}}>
                            By clicking “Get Started,” you: <br /> 
                            - Acknowledge our <a href="/terms-of-use" style={{textDecoration: 'underline'}}>Terms of Use</a> and <a href="/privacy" style={{textDecoration: 'underline'}}>Privacy Policy</a> (including the Notice at Collection); <br />
                            - Understand that we will use the information we already maintain about you to process this request; <br />
                            - Consent to receive marketing emails about loans and other personal-finance-related services from us and our <a href="/partners" style={{textDecoration: 'underline'}}>Marketing Partners</a>.
                        </div>
                    </form>
                </div>

                {/* Footer elements outside glass container */}
                <div className={styles.formFooter}>
                    <div className={styles.buttonContainer}>
                        <LoadingButton
                            endIcon={<></>}
                            loading={loadingButton}
                            loadingPosition="end"
                            variant="contained"
                            size="large"
                            style={newBtnSubmitCustom}
                            onClick={handleSubmit(onSubmit)}
                            type="submit"
                            className={styles.submitBtn}
                        >
                            <span>NEXT STEP</span>
                            <svg width="35" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.arrowIcon}>
                                <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 5L19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </LoadingButton>
                    </div>

                    <div className={styles.securityBadges}>
                        <img src={sectigoSecured} alt="Secured by Sectigo" className={styles.badgeImg} />
                        <img src={sslSecured} alt="SSL Secured" className={styles.badgeImg} />
                        <div className={styles.olaBadge}>OLA</div>
                        <img src={securedCertificate} alt="Secured Certificate" className={styles.badgeImg} />
                    </div>

                    <p className={styles.disclaimer}>
                        APR Ranges From 4.95% up to 35.95% For Qualified Customers<br />
                        90 Day Minimum up to 84 Month Maximum Repayment Period
                    </p>
                </div>
            </div>
        </>
    )
}

export default HeroForm
